import { filter, map, switchMap, take } from 'rxjs/operators';
import { Observable, pipe, tap } from 'rxjs';
import { isEqual } from 'lodash-es';
import { select } from '@ngrx/store';
import { AbstractControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef } from '@angular/core';

import { AsyncData, AsyncStatus, pickAsyncData } from '@iss/wo-common/types';

export const selectDefined = <T, K>(mapFn: (state: T) => K) =>
  pipe(select(mapFn), filter(Boolean));

export const selectNotDefined = <T, K>(mapFn: (state: T) => K) =>
  pipe(select(mapFn), filter(value => !value));

export const selectAsyncData = <T, K, F = K>(
  mapFn: (state: T) => AsyncData<K>,
  fallback: F,
) =>
  pipe(
    select(mapFn),
    map((asyncData) => pickAsyncData(asyncData, fallback)),
  );

export const selectIsAsyncDataLoading = <T, K = unknown>(
  mapFn: (state: T) => AsyncData<K>,
) =>
  pipe(
    select(mapFn),
    map(({ status }) => [ AsyncStatus.INITIAL, AsyncStatus.LOADING ].includes(status)),
  );

export const connectToStore = <T>(
  destroyRef: DestroyRef,
  control: AbstractControl,
  selector: Observable<unknown>,
  dispatcher: (value: T) => unknown,
): void => {
  selector.pipe(
    filter(value => !isEqual(value, control.value)),
    tap(value => {
      control.reset(value, { emitEvent: false });
      control.patchValue(value);
    }),
    takeUntilDestroyed(destroyRef),
  ).subscribe();

  control.valueChanges.pipe(
    switchMap((value) => selector.pipe(
      take(1),
      filter(selectVal => !isEqual(value, selectVal)),
      map(() => value as T),
    )),
    tap(dispatcher),
    takeUntilDestroyed(destroyRef),
  ).subscribe();
};
